import { useCallback } from 'react';
import { useWizard } from 'react-use-wizard';

import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import type { GuidedPlanningPreferences } from '@shared/guidedPlanningPreferences';

type PreferenceKey = keyof GuidedPlanningPreferences;

interface UsePreferenceStepHandlersOptions<K extends PreferenceKey> {
  preferenceKey: K;
  value: GuidedPlanningPreferences[K] | undefined;
}

export function usePreferenceStepHandlers<K extends PreferenceKey>({
  preferenceKey,
  value,
}: UsePreferenceStepHandlersOptions<K>) {
  const { nextStep, previousStep } = useWizard();
  const { session } = useSession();
  const { updateStepData, isSaving } = useSessionMutations();

  const sessionValue = session?.stepData?.preferences?.[preferenceKey];
  const hasChanged = value !== undefined && value !== sessionValue;

  const savePreference = useCallback(async () => {
    if (hasChanged) {
      await updateStepData({
        preferences: {
          ...session?.stepData?.preferences,
          [preferenceKey]: value,
        },
      });
    }
  }, [hasChanged, updateStepData, session?.stepData?.preferences, preferenceKey, value]);

  const handleNext = useCallback(async () => {
    await savePreference();
    nextStep();
  }, [savePreference, nextStep]);

  const handleBack = useCallback(async () => {
    await savePreference();
    previousStep();
  }, [savePreference, previousStep]);

  return {
    handleNext,
    handleBack,
    isSaving,
    sessionValue,
  };
}
